export const Role = {
  SUPER_ADMIN: 'SUPER_ADMIN',
  ADMIN: 'ADMIN',
  ORG_ADMIN: 'ORG_ADMIN',
  ORG_MANAGER: 'ORG_MANAGER',
  ORG_ASSESSOR: 'ORG_ASSESSOR',
  ORG_VIEWER: 'ORG_VIEWER',
  ORG_CONSULTANT: 'ORG_CONSULTANT',
  ORG_AUDITOR: 'ORG_AUDITOR',
  ORG_CISO: 'ORG_CISO',
  ORG_COMPLIANCE_MANAGER: 'ORG_COMPLIANCE_MANAGER',
  ORG_RISK_MANAGER: 'ORG_RISK_MANAGER',
  ORG_POLICY_MANAGER: 'ORG_POLICY_MANAGER',
  ORG_INTERNAL_AUDITOR: 'ORG_INTERNAL_AUDITOR',
  ORG_EXTERNAL_AUDITOR: 'ORG_EXTERNAL_AUDITOR',
  ORG_LEAD_QSA: 'ORG_LEAD_QSA',
  ORG_QSA_SUBCONTRACTOR: 'ORG_QSA_SUBCONTRACTOR',
  ORG_USER: 'ORG_USER',
  VENDOR: 'VENDOR',
  VENDOR_COLLABORATOR: 'VENDOR_COLLABORATOR',
} as const

export type Role = keyof typeof Role

// Organisation Features
export const OrgFeature = {
  USERS: 'USERS',
  STAFF: 'STAFF',
  CTRA: 'CTRA',
  AI_MODEL: 'AI_MODEL',
  SHAREPOINT_CONFIG: 'SHAREPOINT_CONFIG',
  GOOGLE_DRIVE: 'GOOGLE_DRIVE',
  RISK_ASSESSMENT: 'RISK_ASSESSMENT',
  PEOPLE_RISK_ASSESSMENT: 'PEOPLE_RISK_ASSESSMENT',
  POLICY: 'POLICY',
  PROJECT: 'PROJECT',
  SERVICE_PROVIDER: 'SERVICE_PROVIDER',
  DOCUMENT_REPOSITORY: 'DOCUMENT_REPOSITORY',
  RISK: 'RISK',
  INFORMATION_ASSET: 'INFORMATION_ASSET',
  RUN_ZERO_ASSET: 'RUN_ZERO_ASSET',
  PALO_ALTO: 'PALO_ALTO',
  MS_DEFENDER: 'MS_DEFENDER',
  VANTA: 'VANTA',
  DRATA: 'DRATA',
  MIMECAST: 'MIMECAST',
  INCIDENT_REGISTER: 'INCIDENT_REGISTER',
  ASSESSMENTS: 'ASSESSMENTS',
  REPORT_LIBRARY: 'REPORT_LIBRARY',
  SUB_ORGANISATION: 'SUB_ORGANISATION',
  COUNTRY_REGULATORY_REGISTER: 'COUNTRY_REGULATORY_REGISTER',
  EXEMPTIONS_REGISTER: 'EXEMPTIONS_REGISTER',
  ORGANISATIONAL_CONTEXT: 'ORGANISATIONAL_CONTEXT',
  CHANGE_MANAGEMENT_PROCESS: 'CHANGE_MANAGEMENT_PROCESS',
  MEETING_MINUTES: 'MEETING_MINUTES',
  ZAPIER: 'ZAPIER',
  PHISHED: 'PHISHED',
  AZURE: 'AZURE',
  CONFLUENCE: 'CONFLUENCE',
  N8N: 'N8N',
  AI_REGISTER: 'AI_REGISTER',
  EMAIL_CUSTOMIZATION: 'EMAIL_CUSTOMIZATION',
  INFORMATION_SECURITY_OBJECTIVE: 'INFORMATION_SECURITY_OBJECTIVE',
  ISMS_ASSURANCE_CALENDAR: 'ISMS_ASSURANCE_CALENDAR',
} as const

export type OrgFeature = keyof typeof OrgFeature

// Admin Features
export const AdminFeature = {
  ADMIN_STANDARDS: 'ADMIN_STANDARDS',
  ADMIN_CONTROLS: 'ADMIN_CONTROLS',
  ADMIN_MATURITY_MODELS: 'ADMIN_MATURITY_MODELS',
  ADMIN_SECURITY_QUESTION_GROUPS: 'ADMIN_SECURITY_QUESTION_GROUPS',
  ADMIN_SECURITY_QUESTIONNAIRES: 'ADMIN_SECURITY_QUESTIONNAIRES',
  ADMIN_ORGANISATION: 'ADMIN_ORGANISATION',
  ADMIN_ADMIN_MANAGE: 'ADMIN_ADMIN_MANAGE',
  ADMIN_CONTACT_MESSAGE: 'ADMIN_CONTACT_MESSAGE',
  ADMIN_RESOURCE: 'ADMIN_RESOURCE',
  ADMIN_AI_MODEL: 'ADMIN_AI_MODEL',
  ADMIN_IT_PRIVILEGED_OPERATIONS: 'ADMIN_IT_PRIVILEGED_OPERATIONS',
  ADMIN_RISK_ASSESSMENT: 'ADMIN_RISK_ASSESSMENT',
  ADMIN_PEOPLE_RISK_ASSESSMENT: 'ADMIN_PEOPLE_RISK_ASSESSMENT',
  ADMIN_EMAIL_CUSTOMIZATION: 'ADMIN_EMAIL_CUSTOMIZATION',
} as const

export type AdminFeature = keyof typeof AdminFeature

// Combined Feature type for backward compatibility
export const Feature = {
  ...OrgFeature,
  ...AdminFeature,
} as const

export type Feature = keyof typeof Feature

export const PermissionLevel = {
  VIEW: 'VIEW',
  ADD: 'ADD',
  EDIT: 'EDIT',
  DELETE: 'DELETE',
} as const

export type PermissionLevel = keyof typeof PermissionLevel

export const SecurityQuestionGroupType = {
  SERVICE_TYPE: 'SERVICE_TYPE',
  GLOBAL: 'GLOBAL',
  CUSTOM: 'CUSTOM',
} as const

export type SecurityQuestionGroupType = keyof typeof SecurityQuestionGroupType

export const SecurityQuestionType = {
  YES_NO: 'YES_NO',
  EVIDENCE: 'EVIDENCE',
  TEXT: 'TEXT',
  NUMBER: 'NUMBER',
  CHECKBOX: 'CHECKBOX',
} as const

export type SecurityQuestionType = keyof typeof SecurityQuestionType

export const ControlQuestionnaireType = {
  TEXT: 'TEXT',
  YESNO: 'YESNO',
  DATE: 'DATE',
  OPTIONS: 'OPTIONS',
  CHOICES: 'CHOICES',
} as const

export type ControlQuestionnaireType = keyof typeof ControlQuestionnaireType

export const ModuleCategory = {
  GOVERNANCE: 'GOVERNANCE',
  RISK: 'RISK',
  COMPLIANCE: 'COMPLIANCE',
  NONE: 'NONE',
}

export type ModuleCategory = keyof typeof ModuleCategory

export const Frequency = {
  DAILY: 'DAILY',
  WEEKLY: 'WEEKLY',
  MONTHLY: 'MONTHLY',
  QUARTERLY: 'QUARTERLY',
  HALF_YEARLY: 'HALF_YEARLY',
  YEARLY: 'YEARLY',
  NONE: 'NONE',
}

export type Frequency = keyof typeof Frequency

export const WeekDays = {
  SUNDAY: 'SUNDAY',
  MONDAY: 'MONDAY',
  TUESDAY: 'TUESDAY',
  WEDNESDAY: 'WEDNESDAY',
  THURSDAY: 'THURSDAY',
  FRIDAY: 'FRIDAY',
  SATURDAY: 'SATURDAY',
}

export type WeekDays = keyof typeof WeekDays

export const Months = {
  JANUARY: 'JANUARY',
  FEBRUARY: 'FEBRUARY',
  MARCH: 'MARCH',
  APRIL: 'APRIL',
  MAY: 'MAY',
  JUNE: 'JUNE',
  JULY: 'JULY',
  AUGUST: 'AUGUST',
  SEPTEMBER: 'SEPTEMBER',
  OCTOBER: 'OCTOBER',
  NOVEMBER: 'NOVEMBER',
  DECEMBER: 'DECEMBER',
}
export type Months = keyof typeof Months

export const PolicyStatus = {
  DRAFT: 'DRAFT',
  REVIEW_REQUESTED: 'REVIEW_REQUESTED',
  REVIEW: 'REVIEW',
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
  ARCHIVED: 'ARCHIVED',
} as const

export type PolicyStatus = keyof typeof PolicyStatus

export const PolicyGenerationMethod = {
  AI: 'AI',
  URL: 'URL',
  FILE: 'FILE',
  MANUAL: 'MANUAL',
} as const

export type PolicyGenerationMethod = keyof typeof PolicyGenerationMethod

export const RiskTreatment = {
  AVOID: 'AVOID',
  ACCEPT: 'ACCEPT',
  MITIGATE: 'MITIGATE',
  TRANSFER: 'TRANSFER',
  NONE: 'NONE',
} as const

export type RiskTreatment = keyof typeof RiskTreatment

export const ActivityAction = {
  CREATE: 'CREATE',
  READ: 'READ',
  UPDATE: 'UPDATE',
  DELETE: 'DELETE',
  APPROVE: 'APPROVE',
} as const

export type ActivityAction = keyof typeof ActivityAction

export const ActivityModule = {
  USER: 'USER',
  ORGANISATION: 'ORGANISATION',
  SUB_ORGANISATION: 'SUB_ORGANISATION',
  ORGANISATION_USER: 'ORGANISATION_USER',
  FEATURE_PERMISSION: 'FEATURE_PERMISSION',
  USER_PREFERENCE: 'USER_PREFERENCE',
  MATURITY_MODEL: 'MATURITY_MODEL',
  MATURITY_LEVEL: 'MATURITY_LEVEL',
  STANDARD: 'STANDARD',
  SECURITY_QUESTION_GROUP: 'SECURITY_QUESTION_GROUP',
  SECURITY_QUESTION: 'SECURITY_QUESTION',
  SECURITY_QUESTION_OPTION: 'SECURITY_QUESTION_OPTION',
  CONTROL_GROUP: 'CONTROL_GROUP',
  CONTROL: 'CONTROL',
  CONTROL_QUESTIONNAIRE: 'CONTROL_QUESTIONNAIRE',
  STATIC_ATTRIBUTE: 'STATIC_ATTRIBUTE',
  STATIC_VALUE: 'STATIC_VALUE',
  STATIC_VALUE_MAPPING: 'STATIC_VALUE_MAPPING',
  FILE_REPOSITORY: 'FILE_REPOSITORY',
  FOLDER: 'FOLDER',
  FILE: 'FILE',
  FILE_SHARING: 'FILE_SHARING',
  FILE_OPERATION: 'FILE_OPERATION',
  MODULE: 'MODULE',
  ORGANISATION_MODULE: 'ORGANISATION_MODULE',
  ORGANISATION_STANDARD: 'ORGANISATION_STANDARD',
  POLICY_CATEGORY: 'POLICY_CATEGORY',
  POLICY_REVIEW_FREQUENCY: 'POLICY_REVIEW_FREQUENCY',
  TASK_STATUS: 'TASK_STATUS',
  TASK_SEVERITY: 'TASK_SEVERITY',
  RISK_OWNER: 'RISK_OWNER',
  RISK_IMPACT: 'RISK_IMPACT',
  RISK_RATING: 'RISK_RATING',
  RISK_CATEGORY: 'RISK_CATEGORY',
  RISK_LIKELIHOOD: 'RISK_LIKELIHOOD',
  RISK_IDENTIFICATION_SOURCE: 'RISK_IDENTIFICATION_SOURCE',
  IMPORTANCE: 'IMPORTANCE',
  AFFECTED_AREA: 'AFFECTED_AREA',
  ACTION_STATUS: 'ACTION_STATUS',
  SERVICE_PROVIDER_CONTRACT_STATUS: 'SERVICE_PROVIDER_CONTRACT_STATUS',
  ASSESSMENT_TIER_SENSITIVITY: 'ASSESSMENT_TIER_SENSITIVITY',
  ASSESSMENT_TIER_CRITICALITY: 'ASSESSMENT_TIER_CRITICALITY',
  ASSESSMENT_TIER_MAPPINGS: 'ASSESSMENT_TIER_MAPPINGS',
  ASSESSMENT_TIER: 'ASSESSMENT_TIER',
  ROOT_IDENTIFICATION: 'ROOT_IDENTIFICATION',
  STAFF: 'STAFF',
  CTRA: 'CTRA',
  AI_MODEL: 'AI_MODEL',
  SHAREPOINT_CONFIG: 'SHAREPOINT_CONFIG',
  GOOGLE_DRIVE: 'GOOGLE_DRIVE',
  ASSET_TYPE: 'ASSET_TYPE',
  INFORMATION_ASSET: 'INFORMATION_ASSET',
  POLICY: 'POLICY',
  RISK_ASSESSMENT: 'RISK_ASSESSMENT',
  PEOPLE_RISK_ASSESSMENT: 'PEOPLE_RISK_ASSESSMENT',
  SERVICE_TYPE: 'SERVICE_TYPE',
  CERTIFICATION_LINK: 'CERTIFICATION_LINK',
  SERVICE_PROVIDER: 'SERVICE_PROVIDER',
  PROJECT: 'PROJECT',
  TASK: 'TASK',
  TASK_OCCURRENCE: 'TASK_OCCURRENCE',
  SUPPORT_TICKET: 'SUPPORT_TICKET',
  TASK_STATUS_HISTORY: 'TASK_STATUS_HISTORY',
  RISK: 'RISK',
  INFORMATION_SECURITY_OBJECTIVE: 'INFORMATION_SECURITY_OBJECTIVE',
  ISMS_ASSURANCE_CALENDAR: 'ISMS_ASSURANCE_CALENDAR',
  RISK_THREAT: 'RISK_THREAT',
  RISK_VULNERABILITY: 'RISK_VULNERABILITY',
  RISK_REMEDIATION_PLAN: 'RISK_REMEDIATION_PLAN',
  CUSTOM_RISK_CONTROL: 'CUSTOM_RISK_CONTROL',
  VULNERABILITY: 'VULNERABILITY',
  THREAT_CATEGORY: 'THREAT_CATEGORY',
  THREAT_SOURCE: 'THREAT_SOURCE',
  THREAT_ACTOR: 'THREAT_ACTOR',
  THREAT: 'THREAT',
  HELPFUL_TIP: 'HELPFUL_TIP',
  MEETING_MINUTES: 'MEETING_MINUTES',
  ZAPIER: 'ZAPIER',
  PHISHED: 'PHISHED',
  PALO_ALTO: 'PALO_ALTO',
  MS_DEFENDER: 'MS_DEFENDER',
  VANTA: 'VANTA',
  DRATA: 'DRATA',
  MIMECAST: 'MIMECAST',
  N8N: 'N8N',
  AI_REGISTER: 'AI_REGISTER',
  COUNTRY_REGULATORY_REGISTER: 'COUNTRY_REGULATORY_REGISTER',
  JURISDICTION: 'JURISDICTION',
  REGION: 'REGION',
  OPERATION_TYPE: 'OPERATION_TYPE',
  CORPORATE_OR_COMPANIES_LAW: 'CORPORATE_OR_COMPANIES_LAW',
  PRIVACY_OR_DATA_LAW: 'PRIVACY_OR_DATA_LAW',
  CYBERSECURITY_OR_CRITICAL_INFRASTRUCTURE_LAW: 'CYBERSECURITY_OR_CRITICAL_INFRASTRUCTURE_LAW',
  RISK_TYPE: 'RISK_TYPE',
  EXEMPTIONS_REGISTER: 'EXEMPTIONS_REGISTER',
  ORGANISATIONAL_CONTEXT: 'ORGANISATIONAL_CONTEXT',
  CHANGE_MANAGEMENT_PROCESS: 'CHANGE_MANAGEMENT_PROCESS',
  SUBSCRIPTION: 'SUBSCRIPTION',
  SUBSCRIPTION_HISTORY: 'SUBSCRIPTION_HISTORY',
  EMAIL_SUPER_TEMPLATE: 'EMAIL_SUPER_TEMPLATE',
  ORGANISATION_EMAIL_TEMPLATE: 'ORGANISATION_EMAIL_TEMPLATE',
  INCIDENT: 'INCIDENT',
  ASSESSMENT: 'ASSESSMENT',
  AUDIT: 'AUDIT',
  NON_CONFORMANCE: 'NON_CONFORMANCE',
  OPPORTUNITY_FOR_IMPROVEMENT: 'OPPORTUNITY_FOR_IMPROVEMENT',
  STATEMENT_OF_APPLICABILITY: 'STATEMENT_OF_APPLICABILITY',
  DEPARTMENT: 'DEPARTMENT',
  ORGANISATION_PREFERENCE: 'ORGANISATION_PREFERENCE',
  PASSWORD_POLICY: 'PASSWORD_POLICY',
  PCI_ASSESSMENT: 'PCI_ASSESSMENT',
  PCI_SCOPING: 'PCI_SCOPING',
  ISM_IRAP: 'ISM_IRAP',
  ISM_SCOPING: 'ISM_SCOPING',
  SERVICE_PROVIDER_ASSESSMENT: 'SERVICE_PROVIDER_ASSESSMENT',
  INCOMING_QUESTIONNAIRE: 'INCOMING_QUESTIONNAIRE',
  VENDOR_EVALUATION: 'VENDOR_EVALUATION',
  RUN_ZERO_ASSET: 'RUN_ZERO_ASSET',
  JIRA: 'JIRA',
  AZURE: 'AZURE',
  CONFLUENCE: 'CONFLUENCE',
  BUG_REPORT: 'BUG_REPORT',
  USER_GUIDE: 'USER_GUIDE',
  RELEASE_NOTE: 'RELEASE_NOTE',
  DIGITAL_SIGNATURE: 'DIGITAL_SIGNATURE',
  DOCUMENT_PDF_TEMPLATE: 'DOCUMENT_PDF_TEMPLATE',
  TAG: 'TAG',
  ENTITY_COMMENT: 'ENTITY_COMMENT',
  CLASSIFICATION_LABEL: 'CLASSIFICATION_LABEL',
  FISCAL_YEAR: 'FISCAL_YEAR',
  CSP: 'CSP',
  ATTESTATION: 'ATTESTATION',
  ORGANISATION_API_KEY: 'ORGANISATION_API_KEY',
  REGULATORY_CATEGORY: 'REGULATORY_CATEGORY',
} as const

export type ActivityModule = keyof typeof ActivityModule

export const IncidentStatus = {
  OPEN: 'OPEN',
  IN_PROGRESS: 'IN_PROGRESS',
  CONTAINED: 'CONTAINED',
  RESOLVED: 'RESOLVED',
  CLOSED: 'CLOSED',
} as const

export type IncidentStatus = keyof typeof IncidentStatus

export const IncidentImpact = {
  CONFIDENTIALITY: 'CONFIDENTIALITY',
  INTEGRITY: 'INTEGRITY',
  AVAILABILITY: 'AVAILABILITY',
} as const

export type IncidentImpact = keyof typeof IncidentImpact

export const IncidentSeverity = {
  LOW: 'LOW',
  MEDIUM: 'MEDIUM',
  HIGH: 'HIGH',
  CRITICAL: 'CRITICAL',
} as const

export type IncidentSeverity = keyof typeof IncidentSeverity
export const ComplianceStatus = {
  COMPLIANT: 'COMPLIANT',
  OFI: 'OFI',
  MAJOR_NON_CONFORMITY: 'MAJOR_NON_CONFORMITY',
  MINOR_NON_CONFORMITY: 'MINOR_NON_CONFORMITY',
  NON_APPLICABLE: 'NON_APPLICABLE',
  NOT_ASSESSED: 'NOT_ASSESSED',
} as const

export type ComplianceStatus = keyof typeof ComplianceStatus

/** Compliance statuses a non-conformance can be classified as */
export const NcClassification = {
  MAJOR_NON_CONFORMITY: ComplianceStatus.MAJOR_NON_CONFORMITY,
  MINOR_NON_CONFORMITY: ComplianceStatus.MINOR_NON_CONFORMITY,
} as const

export type NcClassification = keyof typeof NcClassification

export const CSPResponseStatus = {
  FULLY_MET: 'FULLY_MET',
  PARTIALLY_MET: 'PARTIALLY_MET',
  NOT_MET: 'NOT_MET',
  NON_APPLICABLE: 'NON_APPLICABLE',
} as const

export type CSPResponseStatus = keyof typeof CSPResponseStatus

// CMMC findings - shown in the UI instead of compliance status; the backend maps
// each finding to a ComplianceStatus value.
export const CmmcFindings = {
  MET: 'MET',
  NOT_MET: 'NOT_MET',
  NOT_APPLICABLE: 'NOT_APPLICABLE',
} as const

export type CmmcFindings = keyof typeof CmmcFindings

export const CompletionStatusNcOfi = {
  COMPLETE: 'COMPLETE',
  INCOMPLETE: 'INCOMPLETE',
} as const

export type CompletionStatusNcOfi = keyof typeof CompletionStatusNcOfi

export const ActionType = {
  CORRECTIVE_ACTION: 'CORRECTIVE_ACTION',
  IMPROVEMENT: 'IMPROVEMENT',
  TREATMENT: 'TREATMENT',
  NULL: 'NULL',
} as const

export type ActionType = keyof typeof ActionType

export const ExemptionsAndExemptionsRegisterStatus = {
  DRAFT: 'DRAFT',
  UNDER_REVIEW: 'UNDER_REVIEW',
  PENDING_APPROVAL: 'PENDING_APPROVAL',
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
  EXPIRED: 'EXPIRED',
} as const

export type ExemptionsAndExemptionsRegisterStatus = keyof typeof ExemptionsAndExemptionsRegisterStatus

export const NotificationStatus = {
  UNREAD: 'UNREAD',
  READ: 'READ',
} as const

export type NotificationStatus = keyof typeof NotificationStatus

export const ChangeManagementProcessStatus = {
  DRAFT: 'DRAFT',
  UNDER_REVIEW: 'UNDER_REVIEW',
  REVIEWED: 'REVIEWED',
  APPROVED: 'APPROVED',
  IMPLEMENTED: 'IMPLEMENTED',
  CLOSED: 'CLOSED',
} as const

export type ChangeManagementProcessStatus = keyof typeof ChangeManagementProcessStatus

export const ServiceProviderAssessmentTemplateStatus = {
  DRAFT: 'DRAFT',
  REVIEW: 'REVIEW',
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
  EXPIRED: 'EXPIRED',
} as const

export type ServiceProviderAssessmentTemplateStatus = keyof typeof ServiceProviderAssessmentTemplateStatus

export const ServiceProviderAssessmentTemplateAction = {
  CREATED: 'CREATED',
  UPDATED: 'UPDATED',
  DELETED: 'DELETED',
} as const

export type ServiceProviderAssessmentTemplateAction = keyof typeof ServiceProviderAssessmentTemplateAction

export const ServiceProviderAssessmentTemplateQuestionType = {
  TEXT: 'TEXT',
  NUMBER: 'NUMBER',
  DATE: 'DATE',
  CHECKBOX: 'CHECKBOX',
  RADIO: 'RADIO',
  YESNO: 'YESNO',
  TEXT_AREA: 'TEXT_AREA',
  FILE: 'FILE',
  URL: 'URL',
} as const

export type ServiceProviderAssessmentTemplateQuestionType = keyof typeof ServiceProviderAssessmentTemplateQuestionType

export const ServiceProviderAssessmentStatus = {
  DRAFT: 'DRAFT',
  SENT: 'SENT',
  IN_PROGRESS: 'IN_PROGRESS',
  SUBMITTED: 'SUBMITTED',
  REVIEWED: 'REVIEWED',
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
  EXPIRED: 'EXPIRED',
} as const

export type ServiceProviderAssessmentStatus = keyof typeof ServiceProviderAssessmentStatus

export const ServiceProviderAssessmentReminderStatus = {
  PENDING: 'PENDING',
  SENT: 'SENT',
  FAILED: 'FAILED',
} as const

export type ServiceProviderAssessmentReminderStatus = keyof typeof ServiceProviderAssessmentReminderStatus

export const SIAImpactRating = {
  LOW: 'LOW',
  MEDIUM: 'MEDIUM',
  HIGH: 'HIGH',
  CRITICAL: 'CRITICAL',
} as const

export type SIAImpactRating = keyof typeof SIAImpactRating

export const SIAStatus = {
  DRAFT: 'DRAFT',
  IN_REVIEW: 'IN_REVIEW',
  APPROVED: 'APPROVED',
  SUPERSEDED: 'SUPERSEDED',
} as const

export type SIAStatus = keyof typeof SIAStatus

export const SIAImpactDimensionType = {
  OPERATIONAL: 'OPERATIONAL',
  FINANCIAL: 'FINANCIAL',
  REGULATORY: 'REGULATORY',
  REPUTATIONAL: 'REPUTATIONAL',
} as const

export type SIAImpactDimensionType = keyof typeof SIAImpactDimensionType

export const ReleaseNoteType = {
  MAJOR: 'MAJOR',
  MINOR: 'MINOR',
  PATCH: 'PATCH',
  HOTFIX: 'HOTFIX',
} as const

export type ReleaseNoteType = keyof typeof ReleaseNoteType

export const ReleaseNoteCategory = {
  FEATURE: 'FEATURE',
  IMPROVEMENT: 'IMPROVEMENT',
  BUG_FIX: 'BUG_FIX',
  SECURITY: 'SECURITY',
  PERFORMANCE: 'PERFORMANCE',
  BREAKING_CHANGE: 'BREAKING_CHANGE',
  DEPRECATION: 'DEPRECATION',
  OTHER: 'OTHER',
} as const

export type ReleaseNoteCategory = keyof typeof ReleaseNoteCategory

export const BugReportType = {
  BUG: 'BUG',
  FEATURE_REQUEST: 'FEATURE_REQUEST',
} as const

export type BugReportType = keyof typeof BugReportType

export const BugReportStatus = {
  NEW: 'NEW',
  ACKNOWLEDGED: 'ACKNOWLEDGED',
  IN_REVIEW: 'IN_REVIEW',
  IN_PROGRESS: 'IN_PROGRESS',
  RESOLVED: 'RESOLVED',
  REJECTED: 'REJECTED',
  CLOSED: 'CLOSED',
} as const

export type BugReportStatus = keyof typeof BugReportStatus

export const BugReportPriority = {
  LOW: 'LOW',
  MEDIUM: 'MEDIUM',
  HIGH: 'HIGH',
  CRITICAL: 'CRITICAL',
} as const

export type BugReportPriority = keyof typeof BugReportPriority

export const SignupRequestStatus = {
  PENDING: 'PENDING',
  IN_PROGRESS: 'IN_PROGRESS',
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
} as const

export type SignupRequestStatus = keyof typeof SignupRequestStatus

export const SubscriptionStatus = {
  ACTIVE: 'ACTIVE',
  EXPIRED: 'EXPIRED',
  CANCELLED: 'CANCELLED',
  SUSPENDED: 'SUSPENDED',
  PENDING: 'PENDING',
} as const

export type SubscriptionStatus = keyof typeof SubscriptionStatus

export const AuditStatus = {
  AUDIT_REQUESTED: 'AUDIT_REQUESTED',
  COMPLETED: 'COMPLETED',
  CANCELLED: 'CANCELLED',
} as const

export type AuditStatus = keyof typeof AuditStatus

export const AuditDetailStatus = {
  NOT_AUDITED: 'NOT_AUDITED',
  CHANGES_SUBMITTED: 'CHANGES_SUBMITTED',
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
  CHANGES_REQUESTED: 'CHANGES_REQUESTED',
  REQUEST_EVIDENCE: 'REQUEST_EVIDENCE',
} as const

export type AuditDetailStatus = keyof typeof AuditDetailStatus

export const ReviewStatus = {
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
} as const

export type ReviewStatus = keyof typeof ReviewStatus

export const MeetingMinuteStatus = {
  DRAFT: 'DRAFT',
  REVIEW: 'REVIEW',
  APPROVED: 'APPROVED',
  COMPLETED: 'COMPLETED',
  CANCELLED: 'CANCELLED',
  ARCHIVED: 'ARCHIVED',
} as const

export type MeetingMinuteStatus = keyof typeof MeetingMinuteStatus

export const MeetingMinuteReviewHistoryStatus = {
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
} as const

export type MeetingMinuteReviewHistoryStatus = keyof typeof MeetingMinuteReviewHistoryStatus

export const PolicyReviewTask = {
  REVIEWER: 'REVIEWER',
  APPROVER: 'APPROVER',
} as const

export type PolicyReviewTask = keyof typeof PolicyReviewTask

export const PolicyReviewStatus = {
  PENDING: 'PENDING',
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
  CANCELLED: 'CANCELLED',
} as const

export type PolicyReviewStatus = keyof typeof PolicyReviewStatus

export const PolicyReviewHistoryStatus = {
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
  CANCELLED: 'CANCELLED',
} as const

export type PolicyReviewHistoryStatus = keyof typeof PolicyReviewHistoryStatus

export const IntegrationType = {
  RUN_ZERO: 'RUN_ZERO',
  NESSUS: 'NESSUS',
  SHAREPOINT: 'SHAREPOINT',
  GOOGLE_DRIVE: 'GOOGLE_DRIVE',
  MICROSOFT_SENTINEL: 'MICROSOFT_SENTINEL',
  LMNTRIX: 'LMNTRIX',
  PALO_ALTO_NETWORKS: 'PALO_ALTO_NETWORKS',
  SLACK: 'SLACK',
  TEAMS: 'TEAMS',
  JIRA: 'JIRA',
  ZAPIER: 'ZAPIER',
  PHISHED: 'PHISHED',
  N8N: 'N8N',
  AI_REGISTER: 'AI_REGISTER',
  AZURE: 'AZURE',
  CONFLUENCE: 'CONFLUENCE',
  MIMECAST: 'MIMECAST',
  MS_DEFENDER: 'MS_DEFENDER',
  VANTA: 'VANTA',
  DRATA: 'DRATA',
} as const

export type IntegrationType = keyof typeof IntegrationType

export const JiraSyncDirection = {
  GRC_TO_JIRA: 'GRC_TO_JIRA',
  JIRA_TO_GRC: 'JIRA_TO_GRC',
  BIDIRECTIONAL: 'BIDIRECTIONAL',
} as const

export type JiraSyncDirection = keyof typeof JiraSyncDirection

export const ExtentOfRemoteTesting = {
  ALL_TESTING_ON_SITE: 'ALL_TESTING_ON_SITE',
  COMBINATION_ON_SITE_AND_REMOTE: 'COMBINATION_ON_SITE_AND_REMOTE',
  ALL_TESTING_REMOTE: 'ALL_TESTING_REMOTE',
} as const

export type ExtentOfRemoteTesting = keyof typeof ExtentOfRemoteTesting

export const AssessmentCoverage = {
  FULL_ASSESSMENT: 'FULL_ASSESSMENT',
  PARTIAL_ASSESSMENT: 'PARTIAL_ASSESSMENT',
} as const

export type AssessmentCoverage = keyof typeof AssessmentCoverage

export const OverallAssessmentOutcome = {
  COMPLIANT: 'COMPLIANT',
  NON_COMPLIANT: 'NON_COMPLIANT',
  COMPLIANT_WITH_LEGAL_EXCEPTION: 'COMPLIANT_WITH_LEGAL_EXCEPTION',
} as const

export type OverallAssessmentOutcome = keyof typeof OverallAssessmentOutcome

export const PaymentChannels = {
  CARD_PRESENT: 'CARD_PRESENT',
  MAIL_ORDER_TELEPHONE_ORDER: 'MAIL_ORDER_TELEPHONE_ORDER',
  E_COMMERCE: 'E_COMMERCE',
} as const

export type PaymentChannels = keyof typeof PaymentChannels

export const AccountDataFlowEntity = {
  AUTHORIZATION: 'AUTHORIZATION',
  CAPTURE: 'CAPTURE',
  SETTLEMENT: 'SETTLEMENT',
  CHARGEBACK_DISPUTE: 'CHARGEBACK_DISPUTE',
  REFUNDS: 'REFUNDS',
  OTHER: 'OTHER',
} as const

export type AccountDataFlowEntity = keyof typeof AccountDataFlowEntity

export const TestingFindingsStatus = {
  IN_PLACE: 'IN_PLACE',
  NOT_APPLICABLE: 'NOT_APPLICABLE',
  NOT_TESTED: 'NOT_TESTED',
  NOT_IN_PLACE: 'NOT_IN_PLACE',
} as const

export type TestingFindingsStatus = keyof typeof TestingFindingsStatus

export const SAQResponseStatus = {
  IN_PLACE: 'IN_PLACE',
  IN_PLACE_WITH_CCW: 'IN_PLACE_WITH_CCW',
  NOT_APPLICABLE: 'NOT_APPLICABLE',
  NOT_TESTED: 'NOT_TESTED',
  NOT_IN_PLACE: 'NOT_IN_PLACE',
} as const

export type SAQResponseStatus = keyof typeof SAQResponseStatus

export const FolderType = {
  DOCUMENTATION_EVIDENCE: 'DOCUMENTATION_EVIDENCE',
  INTERVIEW_EVIDENCE: 'INTERVIEW_EVIDENCE',
  OTHER_ASSESSMENT_EVIDENCE: 'OTHER_ASSESSMENT_EVIDENCE',
  NETWORK_DIAGRAM: 'NETWORK_DIAGRAM',
  ACCOUNT_DATAFLOW_DIAGRAM: 'ACCOUNT_DATAFLOW_DIAGRAM',
} as const

export type FolderType = keyof typeof FolderType

export const ReportingInstructionType = {
  DOCUMENTATION_EVIDENCE: 'DOCUMENTATION_EVIDENCE',
  INTERVIEW_EVIDENCE: 'INTERVIEW_EVIDENCE',
  OTHER_ASSESSMENT_EVIDENCE: 'OTHER_ASSESSMENT_EVIDENCE',
} as const

export type ReportingInstructionType = keyof typeof ReportingInstructionType

export const PciMethods = {
  COMPENSATING_CONTROL: 'COMPENSATING_CONTROL',
  CUSTOMIZED_APPROACH: 'CUSTOMIZED_APPROACH',
} as const

export type PciMethods = keyof typeof PciMethods

// ISM IRAP enums (align with Prisma IrapImplementationStatus, IrapConsumerStatus)
export const IrapImplementationStatus = {
  NOT_ASSESSED: 'NOT_ASSESSED',
  EFFECTIVE: 'EFFECTIVE',
  ALTERNATE_CONTROL: 'ALTERNATE_CONTROL',
  INEFFECTIVE: 'INEFFECTIVE',
  NO_VISIBILITY: 'NO_VISIBILITY',
  NOT_IMPLEMENTED: 'NOT_IMPLEMENTED',
  NOT_APPLICABLE: 'NOT_APPLICABLE',
} as const

export type IrapImplementationStatus = keyof typeof IrapImplementationStatus

export const IrapConsumerStatus = {
  YES: 'YES',
  NO: 'NO',
  NOT_POSSIBLE: 'NOT_POSSIBLE',
  NOT_APPLICABLE: 'NOT_APPLICABLE',
  NOT_ASSESSED: 'NOT_ASSESSED',
} as const

export type IrapConsumerStatus = keyof typeof IrapConsumerStatus

export const ISMDataClassification = {
  NC: 'NC',
  OFFICIAL_SENSITIVE: 'OFFICIAL_SENSITIVE',
  PROTECTED: 'PROTECTED',
  SECRET: 'SECRET',
  TOP_SECRET: 'TOP_SECRET',
} as const

export type ISMDataClassification = keyof typeof ISMDataClassification

export const ISMCloudServiceModel = {
  IAAS: 'IAAS',
  PAAS: 'PAAS',
  SAAS: 'SAAS',
} as const

export type ISMCloudServiceModel = keyof typeof ISMCloudServiceModel

export const ISMEnvironmentsInScopeValues = {
  ADMINISTATION_ENVIRONMENT: 'ADMINISTATION_ENVIRONMENT',
  CLOUD_PRODUCTION_COMMON_CONTROLS: 'CLOUD_PRODUCTION_COMMON_CONTROLS',
  CLOUD_PRODUCTION_SERVICE_SPECIFIC: 'CLOUD_PRODUCTION_SERVICE_SPECIFIC',
  TEST_DEVELOPMENT: 'TEST_DEVELOPMENT',
  PRODUCTION: 'PRODUCTION',
} as const

export type ISMEnvironmentsInScopeValues = keyof typeof ISMEnvironmentsInScopeValues

export const ServiceProviderAssessmentResponseStatus = {
  PENDING: 'PENDING',
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
} as const

export type ServiceProviderAssessmentResponseStatus = keyof typeof ServiceProviderAssessmentResponseStatus

export const IncomingQuestionnaireStatus = {
  DRAFT: 'DRAFT',
  IN_PROGRESS: 'IN_PROGRESS',
  COMPLETED: 'COMPLETED',
  SUBMITTED: 'SUBMITTED',
  ARCHIVED: 'ARCHIVED',
} as const

export type IncomingQuestionnaireStatus = keyof typeof IncomingQuestionnaireStatus

export const IncomingQuestionnaireQuestionType = {
  TEXT: 'TEXT',
  TEXT_AREA: 'TEXT_AREA',
  NUMBER: 'NUMBER',
  DATE: 'DATE',
  YESNO: 'YESNO',
  RADIO: 'RADIO',
  CHECKBOX: 'CHECKBOX',
  FILE: 'FILE',
  URL: 'URL',
} as const

export type IncomingQuestionnaireQuestionType = keyof typeof IncomingQuestionnaireQuestionType

export const IncomingQuestionnaireResponseReviewStatus = {
  PENDING: 'PENDING',
  ACCEPTED: 'ACCEPTED',
  REJECTED: 'REJECTED',
} as const

export type IncomingQuestionnaireResponseReviewStatus = keyof typeof IncomingQuestionnaireResponseReviewStatus

export const AssessmentType = {
  GAP: 'GAP',
  ROC: 'ROC',
  IRAP: 'IRAP',
  SAQ: 'SAQ',
} as const

export type AssessmentType = keyof typeof AssessmentType

export const StandardName = {
  Pci_Dss_v4: 'PCI DSS v4',
  ISM_2025: 'Information Security Manual-2025',
  Smb1001_2025: 'SMB1001:2025',
  Cmmc: 'CMMC',
  Cis_v81: 'CIS v8.1',
  Soc_2: 'SOC 2',
  Nist_Csf: 'NIST CSF',
  Iso_42001_2023: 'ISO/IEC 42001:2023',
  Iso_27004_2016: 'ISO/IEC 27004:2016',
  Iso_9001_2016: 'AS/NZS ISO 9001:2016',
  Iso_27001_2022: 'ISO/IEC 27001:2022',
  Essential8: 'Essential 8',
  Cps_230: 'CPS 230',
  Cps_234: 'CPS 234',
  Csp: 'NSW Cyber Security Policy 2023-2024',
  Sil: 'Supported Independent Living (SIL) Module',
  ISM_JUNE_2026: 'Information Security Manual-June 2026',
}
export type StandardName = keyof typeof StandardName

// System Security Shared Responsibility Model - Matrix options (Yes, No, N/A)
export const SharedResponsibilityOption = {
  YES: 'YES',
  NO: 'NO',
  N_A: 'N_A',
} as const
export type SharedResponsibilityOption = (typeof SharedResponsibilityOption)[keyof typeof SharedResponsibilityOption]

export const OrganisationPreferenceKey = {
  RISK_TRACKING_ID_PREFIX: 'RISK_TRACKING_ID_PREFIX',
  POLICY_TRACKING_ID_PREFIX: 'POLICY_TRACKING_ID_PREFIX',
  POLICY_SELECTION_STATUS_SCOPE: 'POLICY_SELECTION_STATUS_SCOPE',
  ONBOARDING_VISITED_STEPS: 'ONBOARDING_VISITED_STEPS',
  ONBOARDING_CURRENT_STEP: 'ONBOARDING_CURRENT_STEP',
  BADGE_APPEARANCE: 'BADGE_APPEARANCE',
  CURRENCY: 'CURRENCY',
  TIMEZONE: 'TIMEZONE',
  TABLE_DATE_DISPLAY: 'TABLE_DATE_DISPLAY',
} as const

export type OrganisationPreferenceKey = keyof typeof OrganisationPreferenceKey

// Which policy statuses an organisation wants to pick from in policy dropdowns
export const PolicySelectionStatusScope = {
  ALL: 'ALL',
  APPROVED_ONLY: 'APPROVED_ONLY',
} as const

export type PolicySelectionStatusScope = keyof typeof PolicySelectionStatusScope

export const DEFAULT_POLICY_SELECTION_STATUS_SCOPE: PolicySelectionStatusScope = PolicySelectionStatusScope.ALL

export const POLICY_SELECTION_STATUS_SCOPE_OPTIONS: { value: PolicySelectionStatusScope; label: string }[] = [
  { value: PolicySelectionStatusScope.ALL, label: 'All statuses' },
  { value: PolicySelectionStatusScope.APPROVED_ONLY, label: 'Approved only' },
]

// How an organisation wants colour-coded status, rating and priority badges rendered platform-wide
export const BadgeAppearance = {
  FILLED: 'FILLED',
  OUTLINE: 'OUTLINE',
} as const

export type BadgeAppearance = keyof typeof BadgeAppearance

export const DEFAULT_BADGE_APPEARANCE: BadgeAppearance = BadgeAppearance.FILLED

export const BADGE_APPEARANCE_OPTIONS: { value: BadgeAppearance; label: string }[] = [
  { value: BadgeAppearance.FILLED, label: 'Filled' },
  { value: BadgeAppearance.OUTLINE, label: 'Outline' },
]

// Which record dates an organisation wants shown as columns in list tables
export const TableDateDisplay = {
  CREATED_AT: 'CREATED_AT',
  UPDATED_AT: 'UPDATED_AT',
  BOTH: 'BOTH',
  NONE: 'NONE',
} as const

export type TableDateDisplay = keyof typeof TableDateDisplay

export const DEFAULT_TABLE_DATE_DISPLAY: TableDateDisplay = TableDateDisplay.UPDATED_AT

export const TABLE_DATE_DISPLAY_OPTIONS: { value: TableDateDisplay; label: string }[] = [
  { value: TableDateDisplay.CREATED_AT, label: 'Created' },
  { value: TableDateDisplay.UPDATED_AT, label: 'Updated' },
  { value: TableDateDisplay.BOTH, label: 'Both' },
  { value: TableDateDisplay.NONE, label: 'None' },
]

export const ScopingSignOffStatus = {
  PENDING: 'PENDING',
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
} as const

export type ScopingSignOffStatus = keyof typeof ScopingSignOffStatus

// @deprecated
export const PCIScopingSignOffStatus = {
  PENDING: 'PENDING',
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
} as const

export type PCIScopingSignOffStatus = keyof typeof PCIScopingSignOffStatus

export const AssessmentIndicator = {
  PCI_SCOPING: 'PCI_SCOPING',
} as const

export type AssessmentIndicator = keyof typeof AssessmentIndicator

export const CredentialRegistryStatus = {
  ACTIVE: 'ACTIVE',
  EXPIRY_SOON: 'EXPIRY_SOON',
  EXPIRED: 'EXPIRED',
}

export type CredentialRegistryStatus = keyof typeof CredentialRegistryStatus

export const PCICredentialType = {
  QSA: 'QSA',
  ISA: 'ISA',
} as const

export type PCICredentialType = keyof typeof PCICredentialType

export const EmailModule = {
  DUMMY_EMAIL: 'DUMMY_EMAIL',
  BUG_REPORT: 'BUG_REPORT',
  SIGNUP: 'SIGNUP',
  RELEASE_NOTE: 'RELEASE_NOTE',
  BOOK_DEMO: 'BOOK_DEMO',
  UTILS: 'UTILS',
  PROJECT_TASK: 'PROJECT_TASK',
  SERVICE_PROVIDER_ASSESSMENT: 'SERVICE_PROVIDER_ASSESSMENT',
  POLICY: 'POLICY',
  MEETING_MINUTES: 'MEETING_MINUTES',
  AUDIT: 'AUDIT',
} as const

export type EmailModule = keyof typeof EmailModule

export const EmailTemplateKey = {
  DUMMY_TEST_EMAIL: 'DUMMY_TEST_EMAIL',

  BUG_REPORT_CREATED_CONFIRMATION: 'BUG_REPORT_CREATED_CONFIRMATION',
  BUG_REPORT_CREATED_ALERT: 'BUG_REPORT_CREATED_ALERT',
  BUG_REPORT_STATUS_UPDATE: 'BUG_REPORT_STATUS_UPDATE',

  SIGNUP_REQUEST_CONFIRMATION: 'SIGNUP_REQUEST_CONFIRMATION',
  SIGNUP_REQUEST_ALERT: 'SIGNUP_REQUEST_ALERT',

  RELEASE_NOTE_BROADCAST: 'RELEASE_NOTE_BROADCAST',

  BOOK_DEMO_REQUEST_CONFIRMATION: 'BOOK_DEMO_REQUEST_CONFIRMATION',
  BOOK_DEMO_REQUEST_ALERT: 'BOOK_DEMO_REQUEST_ALERT',

  UTILS_RESET_PASSWORD: 'UTILS_RESET_PASSWORD',
  UTILS_WELCOME_TO_ORGANISATION: 'UTILS_WELCOME_TO_ORGANISATION',
  UTILS_WELCOME_TO_ORGANISATION_WITH_PASSWORD: 'UTILS_WELCOME_TO_ORGANISATION_WITH_PASSWORD',

  TASK_ASSIGNED: 'TASK_ASSIGNED',
  TASK_REVIEW_REQUEST: 'TASK_REVIEW_REQUEST',
  TASK_APPROVAL_REQUEST: 'TASK_APPROVAL_REQUEST',
  TASK_REMINDER: 'TASK_REMINDER',

  SERVICE_PROVIDER_ASSESSMENT_REQUEST: 'SERVICE_PROVIDER_ASSESSMENT_REQUEST',
  SERVICE_PROVIDER_ASSESSMENT_REMINDER: 'SERVICE_PROVIDER_ASSESSMENT_REMINDER',
  SERVICE_PROVIDER_ASSESSMENT_REVIEWED: 'SERVICE_PROVIDER_ASSESSMENT_REVIEWED',

  POLICY_OWNER_REVIEW_REQUEST: 'POLICY_OWNER_REVIEW_REQUEST',
  POLICY_APPROVER_APPROVAL_REQUEST: 'POLICY_APPROVER_APPROVAL_REQUEST',
  POLICY_REMINDER: 'POLICY_REMINDER',
  POLICY_SHARED_FOR_ACKNOWLEDGEMENT: 'POLICY_SHARED_FOR_ACKNOWLEDGEMENT',
  POLICY_OWNER_REJECTED_NOTIFICATION: 'POLICY_OWNER_REJECTED_NOTIFICATION',
  POLICY_OWNER_APPROVAL_ACKNOWLEDGEMENT: 'POLICY_OWNER_APPROVAL_ACKNOWLEDGEMENT',
  POLICY_APPROVAL_REMINDER: 'POLICY_APPROVAL_REMINDER',
  POLICY_REVIEW_REMINDER: 'POLICY_REVIEW_REMINDER',

  MEETING_MINUTES_INVITATION: 'MEETING_MINUTES_INVITATION',
  MEETING_MINUTES_CANCELLED: 'MEETING_MINUTES_CANCELLED',
  MEETING_MINUTES_SHARED: 'MEETING_MINUTES_SHARED',

  AUDIT_COMPLETED: 'AUDIT_COMPLETED',
  AUDIT_REQUESTED: 'AUDIT_REQUESTED',
} as const

export type EmailTemplateKey = keyof typeof EmailTemplateKey

export const PciDssCadenceType = {
  CONTINUOUS: 'CONTINUOUS',
  DAILY: 'DAILY',
  WEEKLY: 'WEEKLY',
  QUARTERLY: 'QUARTERLY',
  EVERY_6_MONTHS: 'EVERY_6_MONTHS',
  EVERY_6_MONTHS_AND_AFTER_CHANGE: 'EVERY_6_MONTHS_AND_AFTER_CHANGE',
  ANNUAL: 'ANNUAL',
  ANNUAL_AND_AFTER_CHANGE: 'ANNUAL_AND_AFTER_CHANGE',
  EVENT_DRIVEN: 'EVENT_DRIVEN',
  PER_RISK_ANALYSIS: 'PER_RISK_ANALYSIS',
  PER_RELEASE: 'PER_RELEASE',
  SUPERSEDED: 'SUPERSEDED',
} as const

export type PciDssCadenceType = keyof typeof PciDssCadenceType

export const PciDssTriggerEvent = {
  STAFF_TERMINATION: 'STAFF_TERMINATION',
  SIGNIFICANT_CHANGE: 'SIGNIFICANT_CHANGE',
  ACCOUNT_LIFECYCLE_CHANGE: 'ACCOUNT_LIFECYCLE_CHANGE',
  NEW_TPSP_ENGAGEMENT: 'NEW_TPSP_ENGAGEMENT',
  SECURITY_CONTROL_FAILURE: 'SECURITY_CONTROL_FAILURE',
  UNEXPECTED_PAN_DETECTED: 'UNEXPECTED_PAN_DETECTED',
  VISITOR_DEPARTURE: 'VISITOR_DEPARTURE',
  MEDIA_TRANSFER: 'MEDIA_TRANSFER',
  MEDIA_DISPOSAL: 'MEDIA_DISPOSAL',
  KEY_LIFECYCLE_EVENT: 'KEY_LIFECYCLE_EVENT',
  ACCESS_REQUEST: 'ACCESS_REQUEST',
  CODE_RELEASE: 'CODE_RELEASE',
  PATCH_AVAILABLE: 'PATCH_AVAILABLE',
  EVENT: 'EVENT',
  NETWORK_OR_NSC_CHANGE: 'NETWORK_OR_NSC_CHANGE',
  SIGNIFICANT_ORGANISATIONAL_CHANGE: 'SIGNIFICANT_ORGANISATIONAL_CHANGE',
  LOG_ANOMALY_DETECTED: 'LOG_ANOMALY_DETECTED',
  PENETRATION_TEST_FINDING: 'PENETRATION_TEST_FINDING',
  CUSTOMER_PENETRATION_TEST_REQUEST: 'CUSTOMER_PENETRATION_TEST_REQUEST',
  NEW_HIRE_OR_CONTRACTOR: 'NEW_HIRE_OR_CONTRACTOR',
  TARGETED_RISK_ANALYSIS_REVIEW: 'TARGETED_RISK_ANALYSIS_REVIEW',
} as const

export type PciDssTriggerEvent = keyof typeof PciDssTriggerEvent

export const PciDssTriggerType = {
  EVENT: 'EVENT',
  RISK_ANALYSIS: 'RISK_ANALYSIS',
} as const

export type PciDssTriggerType = keyof typeof PciDssTriggerType

export const PciDssCalendarBucket = {
  ASSESSMENT: 'assessment',
  TRIGGER_TEMPLATE: 'trigger_template',
  BAU: 'bau',
  SCHEDULED: 'scheduled',
  RISK_SCHEDULED: 'risk_scheduled',
  SUPERSEDED: 'superseded',
} as const

export type PciDssCalendarBucket = keyof typeof PciDssCalendarBucket

export const PciTaskStatus = {
  NOT_STARTED: 'NOT_STARTED',
  IN_PROGRESS: 'IN_PROGRESS',
  EVIDENCE_UPLOADED: 'EVIDENCE_UPLOADED',
  QSA_REVIEW: 'QSA_REVIEW',
  IN_PLACE: 'IN_PLACE',
  GAP: 'GAP',
  NOT_APPLICABLE: 'NOT_APPLICABLE',
} as const

export type PciTaskStatus = keyof typeof PciTaskStatus

export const TaskCategory = {
  GENERAL: 'GENERAL',
  PCI_DSS: 'PCI_DSS',
  PCI_DSS_EVIDENCE_REQUEST: 'PCI_DSS_EVIDENCE_REQUEST',
} as const

export type TaskCategory = keyof typeof TaskCategory

export const PciEvidencePackPriority = {
  HIGH: 'HIGH',
  MEDIUM: 'MEDIUM',
  LOW: 'LOW',
} as const

export type PciEvidencePackPriority = keyof typeof PciEvidencePackPriority

export const PciEvidencePackEffort = {
  LOW: 'LOW',
  MODERATE: 'MODERATE',
  HIGH: 'HIGH',
} as const

export type PciEvidencePackEffort = keyof typeof PciEvidencePackEffort

export const AIProvider = {
  ANTHROPIC: 'ANTHROPIC',
  OPENAI: 'OPENAI',
}

export type AIProvider = keyof typeof AIProvider

export const CspSystemRiskRatings = {
  LOW: 'LOW',
  MEDIUM: 'MEDIUM',
  HIGH: 'HIGH',
  EXTREME: 'EXTREME',
}

export type CspSystemRiskRatings = keyof typeof CspSystemRiskRatings
export const MailLogStatus = {
  SENT: 'SENT',
  FAILED: 'FAILED',
  SKIPPED: 'SKIPPED',
} as const

export type MailLogStatus = keyof typeof MailLogStatus

export const AIRegisterStatus = {
  APPROVED: 'APPROVED',
  CONDITIONAL: 'CONDITIONAL',
  PILOT: 'PILOT',
  UNDER_REVIEW: 'UNDER_REVIEW',
  RESTRICTED: 'RESTRICTED',
  RETIRED: 'RETIRED',
  PROHIBITED: 'PROHIBITED',
} as const

export type AIRegisterStatus = keyof typeof AIRegisterStatus

export const AIRegisterScreeningResultStatus = {
  NORMAL: 'NORMAL',
  MEDIUM: 'MEDIUM',
  HIGH: 'HIGH',
  PROHIBITED: 'PROHIBITED',
  FURTHER_REVIEW_REQUIRED: 'FURTHER_REVIEW_REQUIRED',
} as const

export type AIRegisterScreeningResultStatus = keyof typeof AIRegisterScreeningResultStatus

export const AIRegisterScreeningToolQuestionnaireType = {
  YESNO: 'YESNO',
} as const

export type AIRegisterScreeningToolQuestionnaireType = keyof typeof AIRegisterScreeningToolQuestionnaireType

export const RiskAssessmentQuestionnaireResponseStatus = {
  DRAFT: 'DRAFT',
  SUBMITTED: 'SUBMITTED',
} as const

export type RiskAssessmentQuestionnaireResponseStatus = keyof typeof RiskAssessmentQuestionnaireResponseStatus

export const PeopleRiskAssessmentQuestionnaireResponseStatus = {
  DRAFT: 'DRAFT',
  SUBMITTED: 'SUBMITTED',
} as const

export type PeopleRiskAssessmentQuestionnaireResponseStatus =
  keyof typeof PeopleRiskAssessmentQuestionnaireResponseStatus

export const MeetingMinuteReviewStatus = {
  PENDING: 'PENDING',
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
} as const

export type MeetingMinuteReviewStatus = keyof typeof MeetingMinuteReviewStatus
export const CTRAAssetClassification = {
  PUBLIC: 'PUBLIC',
  INTERNAL: 'INTERNAL',
  CONFIDENTIAL: 'CONFIDENTIAL',
  RESTRICTED: 'RESTRICTED',
} as const

export type CTRAAssetClassification = keyof typeof CTRAAssetClassification

export const CTRAEngagementStatus = {
  DRAFT: 'DRAFT',
  IN_PROGRESS: 'IN_PROGRESS',
  UNDER_REVIEW: 'UNDER_REVIEW',
  COMPLETED: 'COMPLETED',
  ARCHIVED: 'ARCHIVED',
} as const

export type CTRAEngagementStatus = keyof typeof CTRAEngagementStatus

export const CTRASoAStatus = {
  IMPLEMENTED: 'IMPLEMENTED',
  NOT_IMPLEMENTED: 'NOT_IMPLEMENTED',
  PARTIALLY_IMPLEMENTED: 'PARTIALLY_IMPLEMENTED',
} as const

export type CTRASoAStatus = (typeof CTRASoAStatus)[keyof typeof CTRASoAStatus]

export const CTRARatingLevel = {
  VERY_LOW: 'VERY_LOW',
  LOW: 'LOW',
  MODERATE: 'MODERATE',
  HIGH: 'HIGH',
  VERY_HIGH: 'VERY_HIGH',
} as const

export type CTRARatingLevel = keyof typeof CTRARatingLevel

// Qualitative risk appetite levels for a risk category.
export const RiskAppetiteLevel = {
  AVERSE: 'AVERSE',
  MINIMAL: 'MINIMAL',
  CAUTIOUS: 'CAUTIOUS',
  OPEN: 'OPEN',
  FLEXIBLE: 'FLEXIBLE',
} as const

export type RiskAppetiteLevel = keyof typeof RiskAppetiteLevel

export const DocumentPdfType = {
  POLICY: 'POLICY',
  MEETING_MINUTE: 'MEETING_MINUTE',
  GENERIC: 'GENERIC',
} as const

export type DocumentPdfType = (typeof DocumentPdfType)[keyof typeof DocumentPdfType]

// ========================================
// DIGITAL SIGNATURE
// ========================================

export const SignatureMethod = {
  DRAWN: 'DRAWN',
  TYPED: 'TYPED',
  UPLOADED: 'UPLOADED',
  CLICK_WRAP: 'CLICK_WRAP',
} as const

export type SignatureMethod = keyof typeof SignatureMethod

export const SignatureIntent = {
  REVIEWED: 'REVIEWED',
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
  ACKNOWLEDGED: 'ACKNOWLEDGED',
  ATTESTED: 'ATTESTED',
} as const

export type SignatureIntent = keyof typeof SignatureIntent

export const SignatureVerificationLevel = {
  EMAIL_LINK: 'EMAIL_LINK',
  EMAIL_OTP: 'EMAIL_OTP',
  AUTHENTICATED: 'AUTHENTICATED',
  SSO: 'SSO',
} as const

export type SignatureVerificationLevel = keyof typeof SignatureVerificationLevel

export const SignatureRequiredFor = {
  APPROVER: 'APPROVER',
  REVIEWER_AND_APPROVER: 'REVIEWER_AND_APPROVER',
  ALL: 'ALL',
} as const

export type SignatureRequiredFor = keyof typeof SignatureRequiredFor

export const ProjectTag = {
  ASSESSMENT: 'ASSESSMENT',
  RISK: 'RISK',
  'PCI DSS': 'PCI DSS',
  CUSTOM: 'CUSTOM',
} as const

export type ProjectTag = keyof typeof ProjectTag

export const IsmsAssuranceTaskIsoCategory = {
  CLAUSE_4_CONTEXT: 'CLAUSE_4_CONTEXT',
  CLAUSE_5_LEADERSHIP: 'CLAUSE_5_LEADERSHIP',
  CLAUSE_6_PLANNING: 'CLAUSE_6_PLANNING',
  CLAUSE_7_SUPPORT: 'CLAUSE_7_SUPPORT',
  CLAUSE_8_OPERATION: 'CLAUSE_8_OPERATION',
  CLAUSE_9_EVALUATION: 'CLAUSE_9_EVALUATION',
  CLAUSE_10_IMPROVEMENT: 'CLAUSE_10_IMPROVEMENT',
  ANNEX_A5_ORGANIZATIONAL: 'ANNEX_A5_ORGANIZATIONAL',
  ANNEX_A6_PEOPLE: 'ANNEX_A6_PEOPLE',
  ANNEX_A7_PHYSICAL: 'ANNEX_A7_PHYSICAL',
  ANNEX_A8_TECHNOLOGICAL: 'ANNEX_A8_TECHNOLOGICAL',
} as const

export type IsmsAssuranceTaskIsoCategory = keyof typeof IsmsAssuranceTaskIsoCategory

export const IsmsAssuranceTaskFrequency = {
  MONTHLY: 'MONTHLY',
  QUARTERLY: 'QUARTERLY',
  SEMI_ANNUAL: 'SEMI_ANNUAL',
  ANNUAL: 'ANNUAL',
  AS_NEEDED: 'AS_NEEDED',
} as const

export type IsmsAssuranceTaskFrequency = keyof typeof IsmsAssuranceTaskFrequency

export const IsmsAssuranceTaskStatus = {
  OPEN: 'OPEN',
  IN_PROGRESS: 'IN_PROGRESS',
  COMPLETED: 'COMPLETED',
  OVERDUE: 'OVERDUE',
  NOT_APPLICABLE: 'NOT_APPLICABLE',
} as const

export type IsmsAssuranceTaskStatus = keyof typeof IsmsAssuranceTaskStatus

/**
 * Where a risk's residual rating sits against its risk category's tolerance.
 * Compared on `RiskRating.value` (the severity weight), never on `sequence`.
 */
export const RiskToleranceStatus = {
  WITHIN: 'WITHIN',
  EXCEEDS: 'EXCEEDS',
  UNKNOWN: 'UNKNOWN',
} as const

export type RiskToleranceStatus = keyof typeof RiskToleranceStatus
